/**
 * API Service Layer for PostureXe Multi-Service Frontend
 */
import axios, { AxiosInstance, AxiosResponse } from 'axios';

const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '::1']);

function getBrowserOrigin(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.location.origin;
}

function isLocalHostname(hostname: string): boolean {
  return LOCAL_HOSTNAMES.has(hostname.toLowerCase());
}

function resolveHttpBaseUrl(rawUrl?: string): string {
  const browserOrigin = getBrowserOrigin();
  const fallback = browserOrigin || 'http://localhost:8002';
  const configuredUrl = rawUrl?.trim() || fallback;
  const url = new URL(configuredUrl, fallback);

  if (typeof window !== 'undefined') {
    const pageUrl = window.location;

    if (!isLocalHostname(pageUrl.hostname) && isLocalHostname(url.hostname)) {
      return coerceApiBaseToPageSecurity(pageUrl.origin);
    }

    if (pageUrl.protocol === 'https:' && url.protocol === 'http:') {
      url.protocol = 'https:';
    }
  }

  return coerceApiBaseToPageSecurity(url.toString().replace(/\/$/, ''));
}

/** When the SPA is served over HTTPS, never return an http:// API base (mixed active content). */
function coerceApiBaseToPageSecurity(base: string): string {
  if (typeof window === 'undefined' || window.location.protocol !== 'https:') {
    return base;
  }
  if (base.startsWith('http://')) {
    return `https://${base.slice('http://'.length)}`.replace(/\/$/, '');
  }
  return base;
}

/** Resolve at call time so HTTPS upgrades always match the current page. */
export function getApiBaseUrl(): string {
  return resolveHttpBaseUrl(process.env.REACT_APP_API_URL);
}

function toWebSocketUrl(url: URL): string {
  if (url.protocol === 'https:') {
    url.protocol = 'wss:';
  } else if (url.protocol === 'http:') {
    url.protocol = 'ws:';
  }

  return url.toString();
}

const AUTH_TOKEN_KEY = 'posturexe_auth_token';

export function getStoredAuthToken(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  return window.localStorage.getItem(AUTH_TOKEN_KEY);
}

export function setStoredAuthToken(token: string | null): void {
  if (typeof window === 'undefined') {
    return;
  }
  if (token) {
    window.localStorage.setItem(AUTH_TOKEN_KEY, token);
  } else {
    window.localStorage.removeItem(AUTH_TOKEN_KEY);
  }
}

export function getApiUrl(path: string): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${getApiBaseUrl()}${normalizedPath}`;
}

export function getWebSocketUrl(): string {
  const browserOrigin = getBrowserOrigin();
  const configuredUrl = process.env.REACT_APP_WS_URL?.trim();
  const apiBase = getApiBaseUrl();
  const apiUrl = new URL(apiBase);
  const fallbackWsUrl = new URL('/ws', apiBase);
  let wsUrl = new URL(configuredUrl || fallbackWsUrl.toString(), apiBase);

  if (typeof window !== 'undefined') {
    const pageUrl = window.location;

    if (!isLocalHostname(pageUrl.hostname) && isLocalHostname(wsUrl.hostname)) {
      wsUrl = fallbackWsUrl;
    }

    // Secure pages cannot use ws:// (mixed content); always prefer wss on HTTPS.
    if (pageUrl.protocol === 'https:' && wsUrl.protocol === 'ws:') {
      wsUrl.protocol = 'wss:';
    }
  }

  if (!configuredUrl && wsUrl.pathname === '/') {
    wsUrl.pathname = '/ws';
  }

  if (!browserOrigin && !configuredUrl && apiUrl.pathname !== '/') {
    wsUrl.pathname = `${apiUrl.pathname.replace(/\/$/, '')}/ws`;
  }

  return toWebSocketUrl(wsUrl);
}

// Types (exported for component use)
export interface Location {
  id: number;
  name: string;
  city: string;
  address: string;
  contact_email: string;
  contact_phone: string;
  latitude?: number;
  longitude?: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface GeoLocation {
  latitude: number;
  longitude: number;
  accuracy: number | null;
  city: string | null;
  country: string | null;
  last_updated: string | null;
}

export interface DeviceMetrics {
  cpu_usage: number | null;
  memory_usage: number | null;
  gpu_usage: number | null;
  inference_fps: number | null;
  camera_fps: number | null;
  cpu_temperature: number | null;
  gpu_temperature: number | null;
  timestamp: string | null;
}

export interface Device {
  id: number;
  device_id: string;
  name: string;
  model: string;
  status: 'active' | 'inactive' | 'maintenance' | 'error';
  is_online: boolean;
  last_seen: string | null;
  uptime_seconds: number;
  current_state: string | null;
  geo_location: GeoLocation | null;
  latest_metrics: DeviceMetrics | null;
  location_id: number | null;
  /** WAN host for OTA / remote access, from device health-check (optional). */
  ota_public_host?: string | null;
  /** TCP port paired with ota_public_host (optional). */
  ota_public_port?: number | null;
  created_at: string;
  updated_at: string;
}

export interface SystemOverview {
  timestamp: string;
  locations: { total: number };
  devices: {
    total: number;
    active: number;
    online: number;
    offline: number;
  };
  today: {
    sessions: number;
    analyses: number;
    average_score: number | null;
  };
  alerts: {
    recent: number;
    critical: number;
  };
}

export interface AnalyticsData {
  period: {
    start_date: string;
    end_date: string;
    days: number;
  };
  filters: {
    location_id?: number;
    device_id?: string;
  };
  summary: {
    total_analyses: number;
    daily_average: number;
    overall_average_score: number | null;
  };
  trends: Array<{
    date: string;
    analyses_count: number;
    average_score: number | null;
    min_score: number | null;
    max_score: number | null;
  }>;
}

// API Client Configuration
class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: '',
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.client.interceptors.request.use((config) => {
      config.baseURL = getApiBaseUrl();
      if (
        typeof config.url === 'string' &&
        typeof window !== 'undefined' &&
        window.location.protocol === 'https:' &&
        config.url.startsWith('http://')
      ) {
        config.url = `https://${config.url.slice('http://'.length)}`;
      }
      const token = getStoredAuthToken();
      if (token) {
        config.headers = config.headers ?? {};
        (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error?.response?.status === 401 && typeof window !== 'undefined') {
          setStoredAuthToken(null);
          const path = window.location.pathname;
          if (!path.startsWith('/login') && !path.startsWith('/results')) {
            window.location.assign('/login');
          }
        }
        return Promise.reject(error);
      }
    );
  }

  async get<T>(url: string, params?: Record<string, any>): Promise<T> {
    const response: AxiosResponse<T> = await this.client.get(url, { params });
    return response.data;
  }

  async post<T>(url: string, data?: any): Promise<T> {
    const response: AxiosResponse<T> = await this.client.post(url, data);
    return response.data;
  }

  async getBlob(url: string, params?: Record<string, any>): Promise<Blob> {
    const response = await this.client.get(url, { params, responseType: 'blob' });
    return response.data as Blob;
  }
}

const apiClient = new ApiClient();

export interface AuthUser {
  id: number;
  username: string;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  user: AuthUser;
}

export const authService = {
  async login(username: string, password: string): Promise<LoginResponse> {
    return apiClient.post<LoginResponse>('/api/auth/login', { username, password });
  },

  async me(): Promise<AuthUser> {
    return apiClient.get<AuthUser>('/api/auth/me');
  },
};

// Device Service
export const deviceService = {
  async getDevices(
    locationId?: number,
    statusFilter?: string,
    includeOffline: boolean = true
  ): Promise<Device[]> {
    // Trailing slash must match FastAPI's mounted route (/api/devices/) so the
    // browser is not 307-redirected (redirects often drop Authorization → 401).
    return apiClient.get<Device[]>('/api/devices/', {
      location_id: locationId,
      status_filter: statusFilter,
      include_offline: includeOffline,
    });
  },

  async getDeviceDetails(deviceId: string): Promise<any> {
    return apiClient.get(`/api/devices/${deviceId}/details`);
  },

  async getDeviceMetrics(deviceId: string, hours: number = 24): Promise<{
    device_id: string;
    metrics_count: number;
    time_range_hours: number;
    metrics: Array<{
      timestamp: string;
      cpu_usage: number;
      memory_usage: number;
      gpu_usage: number;
      gpu_memory_usage: number;
      cpu_temperature: number;
      gpu_temperature: number;
      inference_fps: number;
      camera_fps: number;
      network_latency: number;
    }>;
  }> {
    return apiClient.get(`/api/devices/${deviceId}/metrics`, { hours });
  },
};

// Session Service
export interface Session {
  id: number;
  session_id: string;
  session_type: string;
  device_id: string;
  device_name: string;
  location_name: string | null;
  status: string;
  start_time: string;
  end_time: string | null;
  duration_seconds: number | null;
  analysis_count: number;
  average_score: number | null;
  overall_score: number | null;
  created_at: string;
}

export interface SessionsResponse {
  sessions: Session[];
  total: number;
  completed: number;
  failed: number;
}

export const sessionService = {
  async getSessions(
    deviceId?: string,
    statusFilter?: string,
    startDate?: string,
    endDate?: string,
    skip: number = 0,
    limit: number = 100
  ): Promise<SessionsResponse> {
    const params: Record<string, any> = {
      skip,
      limit,
    };
    
    if (deviceId) params.device_id = deviceId;
    if (statusFilter) params.status_filter = statusFilter;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    
    return apiClient.get<SessionsResponse>('/api/sessions/', params);
  },

  async getSessionDetails(sessionId: string): Promise<any> {
    return apiClient.get(`/api/sessions/${sessionId}/details`);
  },

  async getSessionAnalyses(sessionId: string): Promise<any[]> {
    return apiClient.get(`/api/sessions/${sessionId}/analyses`);
  },
};

// Analytics Service
export const analyticsService = {
  async getSystemOverview(): Promise<SystemOverview> {
    return apiClient.get<SystemOverview>('/api/analytics/overview');
  },

  async getSummaryReport(
    startDate: string,
    endDate: string,
    locationIds?: number[]
  ): Promise<{
    report_info: {
      generated_at: string;
      period: {
        start_date: string;
        end_date: string;
        days: number;
      };
      filters: {
        location_ids: number[] | null;
        locations_count: number;
        devices_count: number;
      };
    };
    summary: {
      total_sessions: number;
      total_analyses: number;
      average_score: number | null;
      unique_users: number;
      total_duration_hours: number;
    };
    location_breakdown: Array<{
      location_id: number;
      location_name: string;
      city: string | null;
      devices_count: number;
      sessions_count: number;
      analyses_count: number;
      average_score: number | null;
      utilization_per_device_per_day: number;
    }>;
    device_performance: Array<{
      device_id: string;
      device_name: string;
      location_id: number;
      metrics_count: number;
      avg_cpu_usage: number;
      avg_memory_usage: number;
      avg_gpu_usage: number;
      avg_inference_fps: number;
      uptime_percentage: number;
      status: string;
    }>;
    trends: Array<{
      date: string;
      analyses_count: number;
      average_score: number | null;
      min_score: number | null;
      max_score: number | null;
    }>;
    alerts: {
      total: number;
      resolved: number;
      unresolved: number;
      by_severity: Record<string, number>;
      by_type: Record<string, number>;
    };
  }> {
    return apiClient.get('/api/analytics/reports/summary', {
      start_date: startDate,
      end_date: endDate,
      location_ids: locationIds,
    });
  },

  async getScoreDistribution(
    startDate: string,
    endDate: string,
    locationIds?: number[]
  ): Promise<{
    categories: {
      excellent: number;
      good: number;
      fair: number;
      poor: number;
      very_poor: number;
    };
    total_sessions: number;
    period: {
      start_date: string;
      end_date: string;
    };
  }> {
    return apiClient.get('/api/analytics/score-distribution', {
      start_date: startDate,
      end_date: endDate,
      location_ids: locationIds,
    });
  },
};

// Alert Service
export const alertService = {
  async acknowledgeAlert(alertId: number): Promise<{ success: boolean; alert_id: number; acknowledged: boolean }> {
    return apiClient.post(`/api/alerts/${alertId}/acknowledge`);
  },
};

// Unified API Service (used by SessionDetails and DeviceDetails pages)
export const apiService = {
  devices: deviceService,
  sessions: sessionService,
  analytics: analyticsService,
  alerts: alertService,

  getDeviceDetails: deviceService.getDeviceDetails,
  getDeviceMetrics: deviceService.getDeviceMetrics,
  getSessionDetails: sessionService.getSessionDetails,
  getSessionAnalyses: sessionService.getSessionAnalyses,
};
